/**
 * Subjects page — table layout with sub-subject and topic navigation.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { listSubjects, createSubject, patchSubject, deleteSubject } from '@/api/resources'
import { Button, PageHeader, EmptyState, Spinner, FIELD_CLS, FIELD_AUTO_CLS, Badge, SearchInput, TableFooter } from '@/components/ui/primitives'
import { fetchAllPages } from '@/utils/fetchAll'

import { Pencil, Trash2, ChevronRight, BookOpen, X } from 'lucide-react'
import ConfirmDialog from '@/components/ConfirmDialog'

const PAGE_SIZE = 20

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800 dark:text-white">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

function ImagePreview({ url, label }) {
  if (!url) return null
  return (
    <div className="mt-1">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <img src={url} alt={label} className="h-16 rounded-lg object-cover border border-gray-200 dark:border-slate-600" />
    </div>
  )
}

function SubjectForm({ initial = {}, allSubjects = [], onSave, onCancel, loading }) {
  const { t } = useTranslation()
  const [name,        setName]        = useState(initial.name || '')
  const [description, setDescription] = useState(initial.description || '')
  const [order,       setOrder]       = useState(initial.order ?? 0)
  const [parentId,    setParentId]    = useState(initial.parent != null ? String(initial.parent) : '')
  const [logo,        setLogo]        = useState(null)
  const [bgImage,     setBgImage]     = useState(null)

  const parentOptions = allSubjects.filter(s => s.id !== initial.id)

  function handleSubmit(e) {
    e.preventDefault()
    const fd = new FormData()
    fd.append('name', name)
    fd.append('description', description)
    fd.append('order', Number(order))
    if (parentId) fd.append('parent', parentId)
    else fd.append('parent', '')
    if (logo)    fd.append('logo', logo)
    if (bgImage) fd.append('background_image', bgImage)
    onSave(fd)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('subjects.name', 'Fan nomi')} *</label>
        <input value={name} onChange={e => setName(e.target.value)} required className={FIELD_CLS} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('subjects.parent', 'Asosiy fan (parent)')}</label>
        <select value={parentId} onChange={e => setParentId(e.target.value)} className={FIELD_CLS}>
          <option value="">— Root (asosiy) fan —</option>
          {parentOptions.map(s => <option key={s.id} value={s.id}>{s.parent ? '  ↳ ' : ''}{s.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.description', 'Tavsif')}</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className={FIELD_CLS} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.order', 'Tartib')}</label>
        <input type="number" value={order} onChange={e => setOrder(e.target.value)} min={0} className={`${FIELD_CLS} w-28`} />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('subjects.logo', 'Logo')}</label>
        <input type="file" accept="image/*" onChange={e => setLogo(e.target.files[0] || null)}
          className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-[var(--coral-tint)] file:text-[var(--coral)] hover:file:bg-[var(--coral-soft)]" />
        <ImagePreview url={initial.logo_url} label="Joriy logo" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('subjects.bgImage', 'Fon rasmi')}</label>
        <input type="file" accept="image/*" onChange={e => setBgImage(e.target.files[0] || null)}
          className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-[var(--coral-tint)] file:text-[var(--coral)] hover:file:bg-[var(--coral-soft)]" />
        <ImagePreview url={initial.background_image_url} label="Joriy fon rasmi" />
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <Button variant="ghost" type="button" onClick={onCancel}>{t('common.cancel', 'Bekor')}</Button>
        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? t('common.saving', 'Saqlanmoqda...') : t('common.save', 'Saqlash')}
        </Button>
      </div>
    </form>
  )
}


export default function SubjectsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.is_superuser || user?.role === 'admin'

  const [allSubjects, setAllSubjects] = useState([])
  const [loading,     setLoading]     = useState(true)
  const [search,      setSearch]      = useState('')
  const [parentFilter,setParentFilter]= useState('root') // 'root' | 'all' | subjectId
  const [page,        setPage]        = useState(1)
  const [modal,       setModal]       = useState(null)
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState(null)
  const [confirm,     setConfirm]     = useState(null)

  const fetchedRef = useRef(false)
  useEffect(() => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    load()
  }, [])
  useEffect(() => { setPage(1) }, [search, parentFilter])

  async function load() {
    setLoading(true)
    try {
      setAllSubjects(await fetchAllPages(listSubjects, { all: 1 }))
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }

  const roots = allSubjects.filter(s => !s.parent)

  const filtered = allSubjects.filter(s => {
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase())
    const matchParent =
      parentFilter === 'all'  ? true :
      parentFilter === 'root' ? !s.parent :
      String(s.parent) === parentFilter
    return matchSearch && matchParent
  })

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  async function handleSave(formData) {
    setSaving(true); setError(null)
    try {
      if (modal === 'create') await createSubject(formData)
      else await patchSubject(modal.id, formData)
      setModal(null)
      await load()
    } catch (e) {
      setError(e.response?.data ? JSON.stringify(e.response.data) : e.message)
    } finally { setSaving(false) }
  }

  async function handleConfirmDelete() {
    try {
      await deleteSubject(confirm)
      await load()
    } catch (e) { setError(e.response?.data?.detail || e.message) }
    finally { setConfirm(null) }
  }

  // Navigate to subfans (filter by parent)
  function goToSubjects(subjectId) {
    setParentFilter(String(subjectId))
    setSearch('')
  }

  // Navigate to topics page filtered by subject
  function goToTopics(subjectId) {
    navigate(`/topics?subject=${subjectId}`)
  }

  const breadcrumb = parentFilter === 'root' || parentFilter === 'all'
    ? null
    : allSubjects.find(s => String(s.id) === parentFilter)

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          breadcrumb
            ? <span className="flex items-center gap-2">
                <button onClick={() => setParentFilter('root')} className="text-[var(--coral)] hover:underline">{t('nav.subjects', 'Fanlar')}</button>
                <ChevronRight size={16} className="text-gray-400" />
                <span>{breadcrumb.name} — Subfanlar</span>
              </span>
            : t('nav.subjects', 'Fanlar')
        }
        actions={
          isAdmin && (
            <Button onClick={() => setModal('create')}>+ {t('subjects.add', "Fan qo'shish")}</Button>
          )
        }
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
      )}

      <div className="flex items-center gap-3">
        <SearchInput value={search} onChange={e => setSearch(e.target.value)} placeholder="Qidirish..." />
        <select value={parentFilter} onChange={e => setParentFilter(e.target.value)} className={FIELD_AUTO_CLS}>
          <option value="root">Asosiy fanlar</option>
          <option value="all">Barchasi</option>
          {roots.map(s => <option key={s.id} value={s.id}>↳ {s.name}</option>)}
        </select>
      </div>

      <div className="tbl-wrap">
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={<BookOpen size={48} />} title="Fan mavjud emas" />
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr className="">
                  <th className="">Fan nomi</th>
                  <th className="">Asosiy fan</th>
                  <th className="">Subfanlar</th>
                  <th className="">Tartib</th>
                  <th className="">Amallar</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(s => {
                  const childCount = allSubjects.filter(c => c.parent === s.id).length
                  const parent = s.parent ? allSubjects.find(p => p.id === s.parent) : null
                  return (
                    <tr key={s.id} className="">
                      <td className="">
                        <div className="flex items-center gap-2.5">
                          {s.logo_url
                            ? <img src={s.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover border border-gray-100 dark:border-slate-700 flex-shrink-0" />
                            : <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0"><span className="text-white font-bold text-xs">{s.name[0]?.toUpperCase()}</span></div>
                          }
                          <span className="primary">{s.name}</span>
                        </div>
                      </td>
                      <td className=""><span className="secondary">{parent?.name || '—'}</span></td>
                      <td className="">
                        {childCount > 0
                          ? <Badge variant="blue">{childCount} ta subfan</Badge>
                          : <span className="secondary">—</span>
                        }
                      </td>
                      <td className=""><span className="secondary">{s.order ?? 0}</span></td>
                      <td className="tr">
                        <div className="flex items-center gap-1 justify-end">
                          {childCount > 0 && (
                            <button onClick={() => goToSubjects(s.id)}
                              className="tbl-btn tbl-go" title="Subfanlarga o'tish">
                              <ChevronRight size={15} />
                            </button>
                          )}
                          <button onClick={() => goToTopics(s.id)}
                            className="tbl-btn tbl-go" title="Mavzularga o'tish">
                            <BookOpen size={15} />
                          </button>
                          {isAdmin && <>
                            <button onClick={() => setModal(s)}
                              className="tbl-btn tbl-edit" title="Tahrirlash">
                              <Pencil size={15} />
                            </button>
                            <button onClick={() => setConfirm(s.id)}
                              className="tbl-btn tbl-danger" title="O'chirish">
                              <Trash2 size={15} />
                            </button>
                          </>}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <TableFooter page={page} totalPages={totalPages} totalCount={filtered.length} onPageChange={setPage} />
          </>
        )}
      </div>

      {modal !== null && (
        <Modal
          title={modal === 'create' ? t('subjects.add', "Fan qo'shish") : t('subjects.edit', 'Fanni tahrirlash')}
          onClose={() => setModal(null)}
        >
          <SubjectForm
            initial={modal === 'create' ? (breadcrumb ? { parent: breadcrumb.id } : {}) : modal}
            allSubjects={allSubjects}
            onSave={handleSave}
            onCancel={() => setModal(null)}
            loading={saving}
          />
        </Modal>
      )}

      {confirm !== null && (
        <ConfirmDialog
          title="O'chirishni tasdiqlang"
          message="Haqiqatan ham o'chirmoqchimisiz?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

